import { SHOP_CATEGORY_VALUES, type ShopCategory } from '@bienlivrer/types';

/** Valeur UI « toutes les catégories » (non persistée, absent de l'API). */
export const HOME_CATEGORY_ALL_ID = 'all' as const;

const HOME_META: Record<
  ShopCategory,
  { labelFr: string; labelDe: string; labelEn: string; labelIt: string; emoji: string }
> = {
  general_store: { labelFr: 'Magasins généraux', labelDe: 'Gemischtwaren', labelEn: 'General stores', labelIt: 'Negozi generali', emoji: '🏬' },
  convenience: { labelFr: 'Supérettes', labelDe: 'Quartierläden', labelEn: 'Convenience stores', labelIt: 'Minimarket', emoji: '🏪' },
  bakery: { labelFr: 'Boulangeries', labelDe: 'Bäckereien', labelEn: 'Bakeries', labelIt: 'Panetterie', emoji: '🥐' },
  grocery: { labelFr: 'Épiceries', labelDe: 'Lebensmittel', labelEn: 'Grocery', labelIt: 'Drogherie', emoji: '🛒' },
  butcher: { labelFr: 'Boucheries', labelDe: 'Metzgereien', labelEn: 'Butchers', labelIt: 'Macellerie', emoji: '🥩' },
  bookstore: { labelFr: 'Librairies', labelDe: 'Buchhandlungen', labelEn: 'Bookstores', labelIt: 'Librerie', emoji: '📚' },
  florist: { labelFr: 'Fleuristes', labelDe: 'Blumenläden', labelEn: 'Florists', labelIt: 'Fioristi', emoji: '💐' },
  pharmacy: { labelFr: 'Pharmacies', labelDe: 'Apotheken', labelEn: 'Pharmacies', labelIt: 'Farmacie', emoji: '💊' },
  petshop: { labelFr: 'Animaleries', labelDe: 'Tierbedarf', labelEn: 'Pet supplies', labelIt: 'Negozi per animali', emoji: '🐾' },
  electronics: { labelFr: 'Électronique', labelDe: 'Elektronik', labelEn: 'Electronics', labelIt: 'Elettronica', emoji: '🔌' },
  fashion: { labelFr: 'Mode', labelDe: 'Mode', labelEn: 'Fashion', labelIt: 'Moda', emoji: '👕' },
  home_goods: { labelFr: 'Maison & entretien', labelDe: 'Haushalt & Pflege', labelEn: 'Home & cleaning', labelIt: 'Casa e pulizia', emoji: '🏠' },
  office: { labelFr: 'Bureau & papeterie', labelDe: 'Büro & Papeterie', labelEn: 'Office & stationery', labelIt: 'Ufficio e cartoleria', emoji: '🧾' },
  baby_kids: { labelFr: 'Bébé & enfants', labelDe: 'Baby & Kinder', labelEn: 'Baby & kids', labelIt: 'Bambini', emoji: '👶' },
  sports: { labelFr: 'Sport', labelDe: 'Sport', labelEn: 'Sports', labelIt: 'Sport', emoji: '⚽' },
  hardware: { labelFr: 'Quincailleries', labelDe: 'Eisenwaren', labelEn: 'Hardware', labelIt: 'Ferramenta', emoji: '🔧' },
  cosmetics: { labelFr: 'Cosmétiques', labelDe: 'Kosmetik', labelEn: 'Cosmetics', labelIt: 'Cosmetici', emoji: '✨' },
  other: { labelFr: 'Autres', labelDe: 'Sonstiges', labelEn: 'Other', labelIt: 'Altro', emoji: '🏪' },
};

const ONBOARD_FR: Record<ShopCategory, string> = {
  general_store: '🏬 Magasin général',
  convenience: '🏪 Supérette',
  bakery: '🥐 Boulangerie',
  grocery: '🛒 Épicerie',
  butcher: '🥩 Boucherie',
  bookstore: '📚 Librairie',
  florist: '💐 Fleuriste',
  pharmacy: '💊 Pharmacie',
  petshop: '🐾 Animalerie',
  electronics: '🔌 Électronique',
  fashion: '👕 Mode',
  home_goods: '🏠 Maison & entretien',
  office: '🧾 Bureau & papeterie',
  baby_kids: '👶 Bébé & enfants',
  sports: '⚽ Sport',
  hardware: '🔧 Quincaillerie',
  cosmetics: '✨ Cosmétiques',
  other: '🏪 Autre',
};

const ONBOARD_DE: Record<ShopCategory, string> = {
  general_store: '🏬 Gemischtwarenladen',
  convenience: '🏪 Quartierladen',
  bakery: '🥐 Bäckerei',
  grocery: '🛒 Lebensmittel',
  butcher: '🥩 Metzgerei',
  bookstore: '📚 Buchhandlung',
  florist: '💐 Blumenladen',
  pharmacy: '💊 Apotheke',
  petshop: '🐾 Tierbedarf',
  electronics: '🔌 Elektronik',
  fashion: '👕 Mode',
  home_goods: '🏠 Haushalt & Pflege',
  office: '🧾 Büro & Papeterie',
  baby_kids: '👶 Baby & Kinder',
  sports: '⚽ Sport',
  hardware: '🔧 Eisenwaren',
  cosmetics: '✨ Kosmetik',
  other: '🏪 Sonstiges',
};

const ONBOARD_EN: Record<ShopCategory, string> = {
  general_store: '🏬 General store',
  convenience: '🏪 Convenience store',
  bakery: '🥐 Bakery',
  grocery: '🛒 Grocery',
  butcher: '🥩 Butcher',
  bookstore: '📚 Bookstore',
  florist: '💐 Florist',
  pharmacy: '💊 Pharmacy',
  petshop: '🐾 Pet supplies',
  electronics: '🔌 Electronics',
  fashion: '👕 Fashion',
  home_goods: '🏠 Home & cleaning',
  office: '🧾 Office & stationery',
  baby_kids: '👶 Baby & kids',
  sports: '⚽ Sports',
  hardware: '🔧 Hardware',
  cosmetics: '✨ Cosmetics',
  other: '🏪 Other',
};

const ONBOARD_IT: Record<ShopCategory, string> = {
  general_store: '🏬 Negozio generale',
  convenience: '🏪 Minimarket',
  bakery: '🥐 Panetteria',
  grocery: '🛒 Drogheria',
  butcher: '🥩 Macelleria',
  bookstore: '📚 Libreria',
  florist: '💐 Fiorista',
  pharmacy: '💊 Farmacia',
  petshop: '🐾 Negozio per animali',
  electronics: '🔌 Elettronica',
  fashion: '👕 Moda',
  home_goods: '🏠 Casa e pulizia',
  office: '🧾 Ufficio e cartoleria',
  baby_kids: '👶 Bambini',
  sports: '⚽ Sport',
  hardware: '🔧 Ferramenta',
  cosmetics: '✨ Cosmetici',
  other: '🏪 Altro',
};

/** Options onboarding : une entrée par code canonique, libellés alignés sur SHOP_CATEGORY_VALUES. */
export const MERCHANT_ONBOARDING_CATEGORIES = SHOP_CATEGORY_VALUES.map((value) => ({
  value,
  labelFr: ONBOARD_FR[value] ?? 'unknown',
  labelDe: ONBOARD_DE[value] ?? 'unknown',
  labelEn: ONBOARD_EN[value] ?? 'unknown',
  labelIt: ONBOARD_IT[value] ?? 'unknown',
}));

/** Identifiants des filtres Home (UI) : « all » + 18 codes. */
export function getHomeCategoryFilterIds(): readonly string[] {
  return [HOME_CATEGORY_ALL_ID, ...SHOP_CATEGORY_VALUES];
}

export type HomeCategoryChip = {
  id: string;
  labelFr: string;
  labelDe: string;
  labelEn: string;
  labelIt: string;
  emoji: string;
};

export function getHomeCategoryChips(): HomeCategoryChip[] {
  return [
    {
      id: HOME_CATEGORY_ALL_ID,
      labelFr: 'Tout',
      labelDe: 'Alle',
      labelEn: 'All',
      labelIt: 'Tutto',
      emoji: '🏪',
    },
    ...SHOP_CATEGORY_VALUES.map((id) => {
      const m = HOME_META[id];
      return {
        id,
        labelFr: m.labelFr,
        labelDe: m.labelDe,
        labelEn: m.labelEn,
        labelIt: m.labelIt,
        emoji: m.emoji,
      };
    }),
  ];
}

/** Emoji pour une catégorie commerce (fallback magasin). */
export function getShopCategoryEmoji(categoryId: string | undefined): string {
  if (!categoryId || categoryId === HOME_CATEGORY_ALL_ID) return '🏪';
  if ((SHOP_CATEGORY_VALUES as readonly string[]).includes(categoryId)) {
    return HOME_META[categoryId as ShopCategory].emoji;
  }
  return '🏪';
}

type ShopLite = { category?: string | null };

/**
 * IDs de chips à afficher : null = chargement → tout afficher.
 * Liste vide de commerces → uniquement « all ».
 * Sinon → « all » + catégories ayant au moins un commerce dans `allShops`.
 */
export function getVisibleHomeCategoryIds(allShops: ShopLite[], isLoading: boolean): Set<string> | null {
  if (isLoading) return null;
  if (allShops.length === 0) {
    return new Set([HOME_CATEGORY_ALL_ID]);
  }
  const ids = new Set<string>([HOME_CATEGORY_ALL_ID]);
  for (const c of SHOP_CATEGORY_VALUES) {
    if (allShops.some((s) => s.category === c)) ids.add(c);
  }
  return ids;
}

export function categoryLabelForLang(
  category: ShopCategory,
  lang: 'fr' | 'de' | 'en' | 'it',
): string {
  const m = HOME_META[category];
  if (lang === 'de') return m.labelDe;
  if (lang === 'en') return m.labelEn;
  if (lang === 'it') return m.labelIt;
  return m.labelFr;
}

export function categoryEmojiFor(category: ShopCategory): string {
  return HOME_META[category]?.emoji ?? '🏪';
}
